/**
 * BulkEmailPanel — right-side drawer for sending a one-shot broadcast
 * via the rep's connected Outlook mailbox.
 *
 * Iter 273 (Jul 2026) — initial ship.
 * Iter 273.1 (Jul 2026) — added inline rich-text editor + audit-log viewer.
 *
 * Gated by:
 *   • Outlook must be connected (parent surfaces the button only when
 *     `status.connected === true`).
 *   • Access code `07936` required on submit (defense-in-depth on top
 *     of the same server-side check).
 *   • 20 mails / user / UTC day, enforced server-side and shown as a
 *     live badge here.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  X, Mail, Loader2, Send, ShieldCheck, ClipboardList, Search,
  Bold, Italic, Underline, Link2, List, ListOrdered, RemoveFormatting,
  ChevronDown, ChevronRight,
} from "lucide-react";

const ACCESS_CODE_LEN = 5;

// ── Rich-text editor ─────────────────────────────────────────────────
// Uses contentEditable + document.execCommand. execCommand is
// deprecated but still universally supported by every browser we
// care about, and it saves us from pulling in ~200KB of Quill/Tiptap
// for what is fundamentally a 5-button toolbar. If a future browser
// ever removes execCommand we can swap this component for a real
// editor without touching the send/pipeline logic.
function RichTextEditor({ value, onChange, testId }) {
  const ref = useRef(null);

  // Sync the DOM once on mount and whenever `value` is externally
  // reset to empty (e.g. after successful send). We intentionally do
  // NOT sync on every value change because that would break the
  // caret position while typing.
  useEffect(() => {
    if (!ref.current) return;
    if (!value && ref.current.innerHTML) {
      ref.current.innerHTML = "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    // eslint-disable-next-line no-restricted-syntax
    document.execCommand(cmd, false, arg);
    onChange?.(ref.current?.innerHTML || "");
  };
  const promptLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const btnStyle = {
    background: "#fff", border: "0.5px solid #E2E8F0", borderRadius: 6,
    padding: "4px 6px", cursor: "pointer", color: "#334155",
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11,
  };

  return (
    <div style={{ border: "0.5px solid #CBD5E1", borderRadius: 8, overflow: "hidden" }}>
      <div
        role="toolbar"
        aria-label="Formatting"
        style={{
          display: "flex", flexWrap: "wrap", gap: 4,
          padding: "6px 8px", background: "#F8FAFC",
          borderBottom: "0.5px solid #E2E8F0",
        }}
      >
        <button type="button" onClick={() => exec("bold")}       title="Bold"          style={btnStyle} data-testid="rte-bold"><Bold style={{ width: 12, height: 12 }} /></button>
        <button type="button" onClick={() => exec("italic")}     title="Italic"        style={btnStyle} data-testid="rte-italic"><Italic style={{ width: 12, height: 12 }} /></button>
        <button type="button" onClick={() => exec("underline")}  title="Underline"     style={btnStyle} data-testid="rte-underline"><Underline style={{ width: 12, height: 12 }} /></button>
        <span style={{ borderLeft: "0.5px solid #E2E8F0", margin: "0 2px" }} />
        <button type="button" onClick={promptLink}                title="Insert link"   style={btnStyle} data-testid="rte-link"><Link2 style={{ width: 12, height: 12 }} /></button>
        <button type="button" onClick={() => exec("insertUnorderedList")} title="Bulleted list" style={btnStyle} data-testid="rte-bullets"><List style={{ width: 12, height: 12 }} /></button>
        <button type="button" onClick={() => exec("insertOrderedList")}   title="Numbered list" style={btnStyle} data-testid="rte-numbers"><ListOrdered style={{ width: 12, height: 12 }} /></button>
        <span style={{ borderLeft: "0.5px solid #E2E8F0", margin: "0 2px" }} />
        <select
          onChange={(e) => { if (e.target.value) exec("formatBlock", e.target.value); e.target.value = ""; }}
          data-testid="rte-heading"
          style={{ ...btnStyle, padding: "3px 4px", cursor: "pointer" }}
          defaultValue=""
        >
          <option value="" disabled>Style</option>
          <option value="p">Paragraph</option>
          <option value="h2">Heading</option>
          <option value="h3">Sub-heading</option>
          <option value="blockquote">Quote</option>
        </select>
        <button type="button" onClick={() => exec("removeFormat")} title="Clear formatting" style={btnStyle} data-testid="rte-clear"><RemoveFormatting style={{ width: 12, height: 12 }} /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testId}
        onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
        onPaste={(e) => {
          // Force plain-text paste so Word/Google-Docs styling doesn't
          // wreck the composed email. The user can still re-apply
          // formatting via the toolbar.
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          // eslint-disable-next-line no-restricted-syntax
          document.execCommand("insertText", false, text);
        }}
        style={{
          minHeight: 200, maxHeight: 360, overflowY: "auto",
          padding: "10px 12px", fontSize: 13, lineHeight: 1.55,
          fontFamily: "Arial, Helvetica, sans-serif", color: "#0F172A",
          outline: "none", background: "#fff",
        }}
      />
    </div>
  );
}

// ── Log viewer ───────────────────────────────────────────────────────
function BulkEmailLogViewer({ token, onClose }) {
  const h = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // { batch_id: true }
  // Iter 273.2 (Jul 2026) — filter controls. Default window is the
  // last 2 days; user can pin an explicit range OR type a subject
  // substring. `days` is ignored the moment `since`/`until` are set.
  const [days, setDays] = useState(2);
  const [since, setSince] = useState("");   // YYYY-MM-DD
  const [until, setUntil] = useState("");   // YYYY-MM-DD
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  // Debounce the subject search so we don't hammer the endpoint on
  // every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 260);
    return () => clearTimeout(t);
  }, [q]);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (since || until) {
        // Explicit range wins — convert calendar dates to ISO
        // day-bounds so "Jul 17 → Jul 19" includes all of Jul 19.
        if (since) params.set("since", new Date(`${since}T00:00:00Z`).toISOString());
        if (until) params.set("until", new Date(`${until}T23:59:59Z`).toISOString());
      } else {
        params.set("days", String(days || 2));
      }
      if (qDebounced) params.set("q", qDebounced);
      const r = await axios.get(`${API}/integrations/m365/email/bulk-log?${params.toString()}`, { headers: h });
      setRows(r.data.rows || []);
    } catch {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, days, since, until, qDebounced]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  // Group per-recipient rows by batch_id so one send-action collapses
  // into a single card the admin can expand. Falls back to a synthetic
  // key for legacy rows (pre-Iter 273.1) that don't carry batch_id.
  const groups = [];
  const seen = new Map();
  for (const r of rows) {
    const key = r.batch_id || `_legacy_${r.sent_at}_${r.subject}`;
    if (!seen.has(key)) {
      const g = {
        key,
        batch_id: r.batch_id,
        subject: r.subject,
        sent_at: r.sent_at,
        sender: r.sender,
        ip: r.ip,
        body_html: r.body_html,
        recipients: [],
      };
      seen.set(key, g);
      groups.push(g);
    }
    seen.get(key).recipients.push({ recipient: r.recipient, success: r.success !== false, error: r.error });
  }

  const resetFilters = () => {
    setDays(2);
    setSince("");
    setUntil("");
    setQ("");
  };
  const hasCustomRange = !!(since || until);

  const chipStyle = (active) => ({
    background: active ? "#2563EB" : "#fff",
    color: active ? "#fff" : "#334155",
    border: `0.5px solid ${active ? "#2563EB" : "#CBD5E1"}`,
    borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
  });

  return (
    <>
      <div
        onClick={onClose}
        data-testid="bulk-email-log-backdrop"
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.42)", backdropFilter: "blur(2px)", zIndex: 10002 }}
      />
      <aside
        data-testid="bulk-email-log-panel"
        style={{
          position: "fixed", top: 20, right: 20, bottom: 20,
          width: "min(1120px, 96vw)", background: "#fff",
          borderRadius: 12, boxShadow: "0 40px 80px -20px rgba(15,23,42,0.4)",
          zIndex: 10003, display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <header style={{ padding: "14px 20px", borderBottom: "0.5px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
            <ClipboardList style={{ width: 16, height: 16, color: "#2563EB" }} /> Bulk email log
            <span style={{ fontSize: 11, fontWeight: 500, color: "#64748B" }}>
              · {groups.length} send{groups.length === 1 ? "" : "s"}
              {(hasCustomRange || qDebounced) && (
                <> · filtered</>
              )}
            </span>
          </div>
          <button onClick={onClose} data-testid="bulk-email-log-close-btn" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#64748B", padding: 4 }} aria-label="Close">
            <X style={{ width: 18, height: 18 }} />
          </button>
        </header>

        {/* ── Filter bar ─────────────────────────────────────────── */}
        <div style={{ padding: "10px 20px", borderBottom: "0.5px solid #E2E8F0", background: "#F8FAFC", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {[
              { label: "2d",  v: 2 },
              { label: "7d",  v: 7 },
              { label: "30d", v: 30 },
              { label: "90d", v: 90 },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => { setDays(opt.v); setSince(""); setUntil(""); }}
                data-testid={`bulk-email-log-range-${opt.v}d`}
                style={chipStyle(!hasCustomRange && days === opt.v)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span style={{ height: 16, width: 1, background: "#E2E8F0" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748B" }}>
            From
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              data-testid="bulk-email-log-since"
              style={{ fontSize: 11, fontFamily: "inherit", padding: "3px 6px", borderRadius: 6, border: "0.5px solid #CBD5E1" }}
            />
            to
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              data-testid="bulk-email-log-until"
              style={{ fontSize: 11, fontFamily: "inherit", padding: "3px 6px", borderRadius: 6, border: "0.5px solid #CBD5E1" }}
            />
          </div>
          <span style={{ height: 16, width: 1, background: "#E2E8F0" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 220 }}>
            <Search style={{ width: 12, height: 12, color: "#64748B" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="bulk-email-log-search"
              placeholder="Search subject…"
              style={{ flex: 1, fontSize: 11, fontFamily: "inherit", padding: "5px 8px", borderRadius: 6, border: "0.5px solid #CBD5E1", background: "#fff" }}
            />
          </div>
          {(hasCustomRange || qDebounced || days !== 2) && (
            <button
              onClick={resetFilters}
              data-testid="bulk-email-log-reset"
              style={{ background: "#fff", border: "0.5px solid #CBD5E1", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#64748B", fontFamily: "inherit" }}
            >
              Reset
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "#F8FAFC" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#64748B", fontSize: 12 }}>
              <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
              Loading log…
            </div>
          ) : groups.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94A3B8", fontSize: 12 }}>
              {(hasCustomRange || qDebounced || days !== 2)
                ? "No sends match the current filters."
                : "No bulk emails sent in the last 2 days."}
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
              {groups.map((g) => {
                const open = !!expanded[g.key];
                const okN = g.recipients.filter((r) => r.success).length;
                const badN = g.recipients.length - okN;
                return (
                  <li key={g.key} data-testid="bulk-email-log-row" style={{ background: "#fff", border: "0.5px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
                    <button
                      onClick={() => setExpanded((s) => ({ ...s, [g.key]: !s[g.key] }))}
                      data-testid="bulk-email-log-row-toggle"
                      style={{
                        width: "100%", textAlign: "left", background: "transparent", border: 0,
                        padding: "10px 14px", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 10, fontFamily: "inherit",
                      }}
                    >
                      {open ? <ChevronDown style={{ width: 14, height: 14, color: "#64748B" }} /> : <ChevronRight style={{ width: 14, height: 14, color: "#64748B" }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {g.subject || "(no subject)"}
                        </div>
                        <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                          {new Date(g.sent_at).toLocaleString()} · From {g.sender || "—"} · IP {g.ip || "—"}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, background: "#ECFDF5", color: "#065F46", border: "0.5px solid #A7F3D0", borderRadius: 999, padding: "2px 8px" }}>
                        {okN} sent
                      </span>
                      {badN > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: "#FEF2F2", color: "#991B1B", border: "0.5px solid #FECACA", borderRadius: 999, padding: "2px 8px" }}>
                          {badN} failed
                        </span>
                      )}
                    </button>
                    {open && (
                      <div style={{ borderTop: "0.5px solid #E2E8F0", background: "#F8FAFC", padding: "10px 14px", fontSize: 11, color: "#334155" }}>
                        <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>
                          Recipients ({g.recipients.length})
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: "none", marginBottom: 12, maxHeight: 200, overflowY: "auto", border: "0.5px solid #E2E8F0", borderRadius: 6, background: "#fff" }}>
                          {g.recipients.map((r, i) => (
                            <li key={i} style={{ padding: "5px 10px", borderBottom: i === g.recipients.length - 1 ? 0 : "0.5px solid #F1F5F9", display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 999, background: r.success ? "#22C55E" : "#EF4444", flexShrink: 0 }} />
                              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.recipient}</span>
                              {r.error && <span style={{ color: "#B91C1C", fontSize: 10, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.error}>{r.error}</span>}
                            </li>
                          ))}
                        </ul>
                        <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 6 }}>Body</div>
                        <div
                          data-testid="bulk-email-log-body"
                          // Server-generated content that we authored ourselves;
                          // recipients set it via the same RTE that WE control.
                          // Displayed inside our own admin panel, viewed only
                          // by the sender / admins.
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: g.body_html || "<em>(empty)</em>" }}
                          style={{
                            background: "#fff", border: "0.5px solid #E2E8F0", borderRadius: 6,
                            padding: "10px 12px", fontSize: 12, lineHeight: 1.55,
                            maxHeight: 280, overflowY: "auto",
                          }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}

export default function BulkEmailPanel({ token, open, onClose, senderEmail }) {
  const h = { Authorization: `Bearer ${token}` };
  const [recipientsRaw, setRecipientsRaw] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState({ used: 0, limit: 20, remaining: 20 });
  const [lastResult, setLastResult] = useState(null);
  const [logOpen, setLogOpen] = useState(false);

  const fetchQuota = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/integrations/m365/email/bulk-quota`, { headers: h });
      setQuota(r.data);
    } catch {
      /* silent — badge just won't update */
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (open) fetchQuota(); }, [open, fetchQuota]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const parseRecipients = (raw) =>
    (raw || "")
      .split(/[,;\n\r\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && s.includes("@"));

  const parsed = parseRecipients(recipientsRaw);
  const parsedCount = parsed.length;
  const canSend =
    !sending &&
    parsedCount > 0 &&
    subject.trim().length > 0 &&
    code.trim().length === ACCESS_CODE_LEN &&
    quota.remaining > 0 &&
    parsedCount <= quota.remaining;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setLastResult(null);
    try {
      const r = await axios.post(
        `${API}/integrations/m365/email/bulk-send`,
        { recipients: parsed, subject: subject.trim(), body_html: body, access_code: code.trim() },
        { headers: h },
      );
      setLastResult(r.data);
      setQuota(r.data.quota || quota);
      if (r.data.sent_count > 0) {
        toast.success(`Sent ${r.data.sent_count} of ${parsedCount}${r.data.failed_count ? ` (${r.data.failed_count} failed)` : ""}`);
        setRecipientsRaw("");
        setSubject("");
        setBody("");
        setCode("");
      } else {
        toast.error("All recipients failed — see per-address results.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && detail.message) || detail || e?.message || "Bulk send failed";
      toast.error(String(msg).slice(0, 220));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        data-testid="bulk-email-backdrop"
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.32)", backdropFilter: "blur(2px)", zIndex: 10000 }}
      />
      <aside
        data-testid="bulk-email-panel"
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(560px, 96vw)", background: "#fff",
          borderLeft: "0.5px solid #E2E8F0",
          zIndex: 10001, display: "flex", flexDirection: "column",
          boxShadow: "-24px 0 60px -20px rgba(15,23,42,0.28)",
        }}
      >
        <header style={{ padding: "16px 20px", borderBottom: "0.5px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 8 }}>
              <Mail style={{ width: 15, height: 15, color: "#2563EB" }} /> Send bulk email
            </div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
              From <strong>{senderEmail || "your connected Outlook"}</strong> · one-shot broadcast
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => setLogOpen(true)}
              data-testid="bulk-email-view-log-btn"
              title="View audit log"
              style={{ background: "#fff", border: "0.5px solid #CBD5E1", borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", color: "#334155", display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <ClipboardList style={{ width: 12, height: 12 }} /> Logs
            </button>
            <button onClick={onClose} data-testid="bulk-email-close-btn" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#64748B", padding: 4 }} aria-label="Close bulk email panel">
              <X style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </header>

        <div style={{ padding: "14px 20px", flex: 1, overflowY: "auto" }}>
          <div
            data-testid="bulk-email-quota"
            style={{
              display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600,
              color: quota.remaining <= 3 ? "#B91C1C" : "#0F766E",
              background: quota.remaining <= 3 ? "#FEF2F2" : "#ECFDF5",
              border: `0.5px solid ${quota.remaining <= 3 ? "#FECACA" : "#A7F3D0"}`,
              borderRadius: 999, padding: "4px 10px", width: "fit-content", marginBottom: 14,
            }}
          >
            <ShieldCheck style={{ width: 12, height: 12 }} />
            {quota.used} / {quota.limit} sent today · {quota.remaining} left
          </div>

          <label style={{ fontSize: 11, fontWeight: 600, color: "#334155", display: "block", marginBottom: 4 }}>
            Recipients{" "}
            <span style={{ color: "#94A3B8", fontWeight: 400 }}>
              (comma, semicolon or new-line separated · <span data-testid="bulk-email-parsed-count">{parsedCount}</span> parsed)
            </span>
          </label>
          <textarea
            value={recipientsRaw}
            onChange={(e) => setRecipientsRaw(e.target.value)}
            data-testid="bulk-email-recipients"
            placeholder="alice@practice.com, bob@clinic.org&#10;carol@hospital.net"
            rows={3}
            style={{
              width: "100%", fontSize: 12, fontFamily: "inherit",
              padding: "8px 10px", borderRadius: 8, border: "0.5px solid #CBD5E1",
              resize: "vertical", boxSizing: "border-box", marginBottom: 12,
            }}
          />

          <label style={{ fontSize: 11, fontWeight: 600, color: "#334155", display: "block", marginBottom: 4 }}>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="bulk-email-subject"
            placeholder="Product update for Q3"
            style={{
              width: "100%", fontSize: 12, fontFamily: "inherit",
              padding: "8px 10px", borderRadius: 8, border: "0.5px solid #CBD5E1",
              boxSizing: "border-box", marginBottom: 12,
            }}
          />

          <label style={{ fontSize: 11, fontWeight: 600, color: "#334155", display: "block", marginBottom: 4 }}>
            Message <span style={{ color: "#94A3B8", fontWeight: 400 }}>(rich text — bold, links, lists)</span>
          </label>
          <RichTextEditor value={body} onChange={setBody} testId="bulk-email-body" />

          <label style={{ fontSize: 11, fontWeight: 600, color: "#334155", display: "block", marginTop: 12, marginBottom: 4 }}>
            Access code <span style={{ color: "#94A3B8", fontWeight: 400 }}>(5 digits · required to send)</span>
          </label>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, ACCESS_CODE_LEN))}
            data-testid="bulk-email-access-code"
            inputMode="numeric"
            autoComplete="off"
            placeholder="•••••"
            style={{ width: 140, fontSize: 13, fontFamily: "monospace", letterSpacing: 4, padding: "8px 10px", borderRadius: 8, border: "0.5px solid #CBD5E1", boxSizing: "border-box" }}
          />

          {quota.remaining > 0 && parsedCount > quota.remaining && (
            <div data-testid="bulk-email-over-quota-warn" style={{ marginTop: 12, fontSize: 11, color: "#B91C1C", background: "#FEF2F2", border: "0.5px solid #FECACA", borderRadius: 8, padding: "8px 10px" }}>
              You entered <strong>{parsedCount}</strong> recipients but have only <strong>{quota.remaining}</strong> daily send{quota.remaining === 1 ? "" : "s"} left. Trim the list to send today.
            </div>
          )}

          {lastResult && (
            <div data-testid="bulk-email-results" style={{ marginTop: 16, border: "0.5px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: "#F8FAFC", fontSize: 11, fontWeight: 700, color: "#0F172A", borderBottom: "0.5px solid #E2E8F0" }}>
                Send results · {lastResult.sent_count} sent · {lastResult.failed_count} failed
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", maxHeight: 200, overflowY: "auto" }}>
                {(lastResult.results || []).map((r, i) => (
                  <li key={i} style={{ padding: "6px 12px", fontSize: 11, display: "flex", alignItems: "center", gap: 8, borderBottom: i === lastResult.results.length - 1 ? 0 : "0.5px solid #F1F5F9", background: r.success ? "#F0FDF4" : "#FEF2F2" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: r.success ? "#22C55E" : "#EF4444", flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#0F172A" }}>{r.recipient}</span>
                    {r.error && <span style={{ color: "#B91C1C", fontSize: 10, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.error}>{r.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <footer style={{ padding: "12px 20px", borderTop: "0.5px solid #E2E8F0", display: "flex", justifyContent: "flex-end", gap: 8, background: "#FAFBFC" }}>
          <button onClick={onClose} disabled={sending} data-testid="bulk-email-cancel-btn" style={{ background: "#fff", border: "0.5px solid #CBD5E1", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: sending ? "not-allowed" : "pointer", color: "#334155", fontFamily: "inherit" }}>Cancel</button>
          <button
            onClick={handleSend}
            disabled={!canSend}
            data-testid="bulk-email-send-btn"
            style={{
              background: canSend ? "#2563EB" : "#CBD5E1",
              color: "#fff", border: 0, borderRadius: 8,
              padding: "8px 18px", fontSize: 12, fontWeight: 700,
              cursor: canSend ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
            }}
          >
            {sending
              ? <><Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> Sending…</>
              : <><Send style={{ width: 13, height: 13 }} /> Send to {parsedCount || 0}</>}
          </button>
        </footer>
      </aside>

      {logOpen && <BulkEmailLogViewer token={token} onClose={() => setLogOpen(false)} />}
    </>
  );
}
